import { interpolate, parseColor, resolveColor } from "../contract/tokens.js";

export const FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

const JUSTIFY = {
  start: "flex-start",
  left: "flex-start",
  center: "center",
  end: "flex-end",
  right: "flex-end",
  "space-between": "space-between",
  "space-around": "space-around",
  "space-evenly": "space-evenly",
};

const ALIGN = {
  start: "flex-start",
  top: "flex-start",
  center: "center",
  end: "flex-end",
  bottom: "flex-end",
};

const OVERLAP_JUSTIFY = {
  start: "start",
  left: "start",
  center: "center",
  end: "end",
  right: "end",
};

const OVERLAP_ALIGN = {
  start: "start",
  top: "start",
  center: "center",
  end: "end",
  bottom: "end",
};

export function resolveBoxSide(value, vars) {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return `${value}px`;
  if (typeof value !== "string") return null;
  const resolved = interpolate(value, vars).trim();
  if (!resolved) return null;
  if (/^-?\d+(\.\d+)?(px|rem|em|%)$/.test(resolved) || resolved === "0") return resolved;
  const numeric = Number(resolved);
  if (Number.isFinite(numeric)) return `${numeric}px`;
  return resolved;
}

export function applyBox(el, box, prefix, vars) {
  if (!box) return;
  for (const side of ["top", "right", "bottom", "left"]) {
    const resolved = resolveBoxSide(box[side], vars);
    if (resolved == null) continue;
    const prop = prefix + side[0].toUpperCase() + side.slice(1);
    el.style[prop] = resolved;
  }
}

function resetFlexUnlessWeighted(el, axis) {
  if (el.dataset.hvFlexAxis && el.dataset.hvFlexAxis !== axis) return;
  el.style.flex = "0 0 auto";
  delete el.dataset.hvFlexAxis;
}

function applySize(el, spec, axis, vars = {}) {
  if (!spec || !spec.type) return;
  if (spec.type === "calculated" && spec.value != null) {
    el.style[axis] = interpolate(String(spec.value), vars);
    resetFlexUnlessWeighted(el, axis);
    return;
  }
  if (typeof spec === "string") {
    const resolved = resolveBoxSide(spec, vars);
    if (resolved) {
      el.style[axis] = resolved;
      resetFlexUnlessWeighted(el, axis);
    }
    return;
  }
  if (spec.type === "match_parent" && spec.weight != null) {
    el.style.flex = `${spec.weight} 1 0`;
    el.style[axis === "width" ? "minWidth" : "minHeight"] = "0";
    el.dataset.hvFlexAxis = axis;
    return;
  }
  if (spec.type === "match_parent") {
    el.style[axis] = "100%";
    if (axis === "width") {
      el.style.minWidth = "0";
      el.style.alignSelf = "stretch";
    }
    if (axis === "height") el.style.minHeight = "100%";
    return;
  }
  if (spec.type === "wrap_content") {
    el.style[axis] = "max-content";
    resetFlexUnlessWeighted(el, axis);
    return;
  }
  if (spec.type === "fixed" && spec.value != null) {
    el.style[axis] = `${spec.value}px`;
    resetFlexUnlessWeighted(el, axis);
    if (axis === "width") {
      el.style.minWidth = "0";
      el.style.flexShrink = "0";
    }
  }
}

export function resolveLayout(node) {
  const layout = node.layout || {};
  return {
    width: layout.width,
    height: layout.height,
    minWidth: layout.min_width,
    minHeight: layout.min_height,
    maxWidth: layout.max_width,
    maxHeight: layout.max_height,
    gap: layout.gap,
    margins: layout.margins,
    paddings: layout.paddings,
    clip: layout.clip === true,
  };
}

export function borderRadius(node) {
  return node.border?.radius ?? node.border?.corner_radius;
}

export function applyBorderRadius(el, radius, vars) {
  if (radius == null) return;
  const resolved = resolveBoxSide(radius, vars);
  if (resolved) el.style.borderRadius = resolved;
}

export function isWidgetVisualRootPath(path) {
  return typeof path === "string" && /^widget:[^/]+\/visual$/.test(path);
}

export function applyLayoutBox(el, node, ctx, options = {}) {
  const layout = resolveLayout(node);
  const vars = ctx?.vars ?? {};
  if (options.width !== false) applySize(el, layout.width, "width", vars);
  if (options.height !== false) applySize(el, layout.height, "height", vars);
  if (options.minWidth !== false) applyMinSize(el, layout.minWidth, "width", ctx.vars);
  if (options.minHeight !== false) applyMinSize(el, layout.minHeight, "height", ctx.vars);
  if (options.maxWidth !== false) applyMaxSize(el, layout.maxWidth, "width", ctx.vars);
  if (options.maxHeight !== false) applyMaxSize(el, layout.maxHeight, "height", ctx.vars);
  if (layout.maxWidth != null) {
    el.style.alignSelf = "center";
    el.style.marginInline = "auto";
  }
  if (options.margins !== false) applyBox(el, layout.margins, "margin", ctx.vars);
  if (options.paddings) applyBox(el, layout.paddings, "padding", ctx.vars);
  if (layout.clip) el.style.overflow = "hidden";
  return layout;
}

function applyMinSize(el, spec, axis, vars) {
  if (spec == null) return;
  const prop = `min${axis[0].toUpperCase()}${axis.slice(1)}`;
  if (typeof spec === "number" && Number.isFinite(spec)) {
    el.style[prop] = `${spec}px`;
    return;
  }
  if (typeof spec === "string") {
    const resolved = resolveBoxSide(spec, vars);
    if (resolved) el.style[prop] = resolved;
    return;
  }
  if (spec.type === "fixed" && spec.value != null) {
    el.style[prop] = `${spec.value}px`;
  }
}

function applyMaxSize(el, spec, axis, vars) {
  if (spec == null) return;
  if (typeof spec === "number" && Number.isFinite(spec)) {
    el.style[`max${axis[0].toUpperCase()}${axis.slice(1)}`] = `${spec}px`;
    return;
  }
  if (typeof spec === "string") {
    const resolved = resolveBoxSide(spec, vars);
    if (resolved) el.style[`max${axis[0].toUpperCase()}${axis.slice(1)}`] = resolved;
    return;
  }
  if (spec.type === "fixed" && spec.value != null) {
    el.style[`max${axis[0].toUpperCase()}${axis.slice(1)}`] = `${spec.value}px`;
  }
}

export function applyDirection(el, direction, node, vars = {}) {
  if (!direction) return;
  if (direction.type === "grid") {
    el.style.display = "grid";
    const layout = node?.layout || {};
    const columns = layout.grid_columns ?? layout.gridColumns;
    if (columns != null) {
      el.style.gridTemplateColumns = interpolate(String(columns), vars);
    }
    el.dataset.layout = "grid";
    return;
  }
  if (direction.type === "overlap") {
    el.style.display = "grid";
    el.style.gridTemplateColumns = "1fr";
    el.style.gridTemplateRows = "1fr";
    el.dataset.layout = "overlap";
    if (OVERLAP_JUSTIFY[direction.horizontal]) el.style.justifyItems = OVERLAP_JUSTIFY[direction.horizontal];
    if (OVERLAP_ALIGN[direction.vertical]) el.style.alignItems = OVERLAP_ALIGN[direction.vertical];
    return;
  }
  el.style.display = "flex";
  const isColumn = direction.type === "vertical";
  el.style.flexDirection = isColumn ? "column" : "row";
  el.style.flexWrap = "nowrap";
  if (direction.horizontal) {
    const prop = isColumn ? "alignItems" : "justifyContent";
    el.style[prop] = JUSTIFY[direction.horizontal] || direction.horizontal;
  }
  if (direction.vertical) {
    const prop = isColumn ? "justifyContent" : "alignItems";
    el.style[prop] = ALIGN[direction.vertical] || direction.vertical;
  }
}

